import {
	FIELD_LAYOUT_ITEM_TYPE,
	type LayoutItemLink,
	type SwiftTabWithLinks,
	toLayoutItemKey,
} from '../../types/layout-items.tsx';
import type { FieldItemFragment as GraphqlFieldItem } from '../fragments/__generated_apollo__/FieldItemFragment';
import type { JiraIssueLayoutItemContainerFragment } from '../fragments/__generated_apollo__/JiraIssueLayoutItemContainerFragment';
import type { Transformers } from './types.tsx';

export type Dependencies = {
	category: Transformers['category'];
};

/**
 * Transform tab data from GraphQL into a SWIFT compatible {@link SwiftTabWithLinks} map keyed by SWIFT category name.
 */
export const transformTabs = (
	containers: ReadonlyArray<JiraIssueLayoutItemContainerFragment>,
	fieldsById: Map<string, GraphqlFieldItem>,
	transformers: Dependencies,
): {
	[fieldCategoryName: string]: SwiftTabWithLinks[];
} => {
	const accumulator: {
		[fieldCategoryName: string]: SwiftTabWithLinks[];
	} = {};

	return containers.reduce((accum, container) => {
		const { containerType, items } = container;

		if (!containerType || !items || !items.nodes) {
			throw new Error('Failed to load initial data. No containers for tabs.');
		}

		const tabsInContainer: SwiftTabWithLinks[] = items.nodes
			.map((node) => {
				if (
					node &&
					node.__typename === 'JiraIssueItemTabContainer' &&
					node.name != null &&
					node.items != null &&
					node.items.nodes != null
				) {
					const { tabContainerId, name } = node;
					const layoutItems: ReadonlyArray<LayoutItemLink> = node.items.nodes
						// TODO MCD-2079 Update schema types so nodes does not allow nullish items
						.filter(Boolean)
						.map((fieldNode) => {
							// TODO: Need to understand & update logic code to fix this issue, e.g. checking null and return a default value
							const matchingFieldItem = fieldsById.get(fieldNode.fieldItemId);

							if (matchingFieldItem) {
								return {
									// The API only allows to put fields in tabs so we can hardcode this one
									layoutItemType: FIELD_LAYOUT_ITEM_TYPE,
									layoutItemKey: toLayoutItemKey(FIELD_LAYOUT_ITEM_TYPE, matchingFieldItem.key),
								};
							}
							// Ignore tab nodes which don't have a matching field item. We don't expect this to happen
							// but if it does we probably don't want to crash the app.
							return null;
						})
						.filter(Boolean);

					return {
						tabContainerId,
						name,
						layoutItems,
					};
				}
				return null;
			})
			.filter(Boolean);

		if (tabsInContainer.length === 0) {
			/*
			 * This part is important.
			 * From SWIFT point of view, there is a difference between "There is <empty list> of tabs" and "There is <null> of tabs"
			 * because they mean different things:
			 * - Empty list of tabs means that there are no tabs at the moment _but there could be_.
			 *         This makes SWIFT render tab UI (in M2 it will have "Add a tab" button) and ignore the root layout items completely.
			 * - "null" tabs means that the category has no tabs feature.
			 *         This makes SWIFT ignore the tabs, and render "root" layout items, as if there were no tabs at all.
			 */
			return accum;
		}

		// Extract category name from container type
		const category = transformers.category(containerType);

		return {
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			...accum,
			[String(category)]: tabsInContainer,
		};
	}, accumulator);
};
